/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore, type AuthUser } from '@/stores/auth-store'
import { getSelf, getWhoAmI } from '@/lib/api'

/**
 * Shared query key for the current user's `/api/user/self` snapshot.
 *
 * Any flow that changes the user's quota/balance (redemption, online topup,
 * subscription redeem, …) can `invalidateQueries({ queryKey: AUTH_SELF_QUERY_KEY })`
 * to force the header / sidebar / wallet balance to refresh immediately.
 */
export const AUTH_SELF_QUERY_KEY = ['auth', 'self'] as const

type SelfResponse = { success?: boolean; data?: AuthUser } | undefined

/**
 * Keeps the persisted `auth.user` snapshot (used by the header/sidebar balance)
 * in sync with the server.
 *
 * Historically `auth.setUser` was only called at login, so `user.quota` was a
 * frozen login-time snapshot: after a topup/redemption the top-right balance
 * kept showing the stale value while pages that re-fetch `/api/user/self`
 * (profile, wallet) showed the correct amount.
 *
 * Mounting this hook once in the authenticated shell refetches the profile
 * on an interval and whenever the window regains focus, writing the fresh
 * user back into the store so every consumer of `auth.user` stays live.
 */
export function useAuthUserSync(): void {
  const user = useAuthStore((state) => state.auth.user)
  const setUser = useAuthStore((state) => state.auth.setUser)

  const { data } = useQuery({
    queryKey: AUTH_SELF_QUERY_KEY,
    queryFn: getSelf,
    // Only poll while authenticated; disabled after logout.
    enabled: Boolean(user),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Refresh at most once a minute; skip while the tab is backgrounded.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  })

  // localStorage is origin-scoped. When a user logs in on api.<domain> and
  // then opens the marketing apex, auth.user starts empty even though the
  // shared HttpOnly session cookie is valid. Probe the session directly so the
  // public header can recover the user without exposing the cookie to JS.
  const { data: whoAmI } = useQuery({
    queryKey: ['auth', 'whoami'],
    queryFn: getWhoAmI,
    enabled: !user,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
  })

  useEffect(() => {
    const res = data as SelfResponse
    if (res?.success && res.data) {
      setUser(res.data)
    }
  }, [data, setUser])

  useEffect(() => {
    if (user || !whoAmI?.success || !whoAmI.data) return
    setUser(whoAmI.data)
  }, [user, setUser, whoAmI])
}
